import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useState, useMemo } from 'react'
import * as Yup from 'yup';
import RequestModal from '../partials/RequestModal';

function Assessment() {
  const [step, setStep] = useState(1);
  const [assessmentResult, setAssessmentResult] = useState(null);

  const stepValidationSchemas = useMemo(() => [
    // Step 1: Demographics
    Yup.object({
      age: Yup.number().required('Age is required').min(18, 'Must be at least 18 years old').max(120, 'Invalid age'),
      gender: Yup.string().required('Please select your gender'),
      location: Yup.string().required('Please select your location type'),
      relationship_status: Yup.string().required('Please select your relationship status')
    }),
    // Step 2: Sexual Health
    Yup.object({
      sexual_partners: Yup.string().required('Please select an option'),
      unprotected_sex: Yup.string().required('Please select an option'),
      partner_hiv_status: Yup.string().required('Please select an option'),
      sex_work: Yup.string().required('Please select an option')
    }),
    // Step 3: Risk Factors
    Yup.object({
      injection_drug_use: Yup.string().required('Please select an option'),
      blood_transfusion: Yup.string().required('Please select an option'),
      tattoo_piercing: Yup.string().required('Please select an option'),
      healthcare_exposure: Yup.string().required('Please select an option')
    }),
    // Step 4: Symptoms & Testing History
    Yup.object({
      recent_illness: Yup.string().required('Please select an option'),
      weight_loss: Yup.string().required('Please select an option'),
      night_sweats: Yup.string().required('Please select an option'),
      previous_test: Yup.string().required('Please select an option'),
      last_test_result: Yup.string().when('previous_test', {
        is: 'yes',
        then: (schema) => schema.required('Please select your last test result')
      })
    })
  ], []);

  const calculateRiskScore = (values) => {
    let score = 0;
    
    // Age factor
    if (values.age >= 15 && values.age <= 49) score += 1;
    
    // Sexual health factors
    if (values.sexual_partners === 'multiple') score += 3;
    if (values.sexual_partners === '2-5') score += 2;
    if (values.unprotected_sex === 'always' || values.unprotected_sex === 'often') score += 3;
    if (values.unprotected_sex === 'sometimes') score += 2;
    if (values.partner_hiv_status === 'positive') score += 4;
    if (values.partner_hiv_status === 'unknown') score += 2;
    if (values.sex_work === 'yes') score += 3;
    
    // Risk factors
    if (values.injection_drug_use === 'yes') score += 4;
    if (values.blood_transfusion === 'yes') score += 2;
    if (values.tattoo_piercing === 'yes') score += 1;
    if (values.healthcare_exposure === 'yes') score += 2;
    
    // Symptoms
    if (values.recent_illness === 'yes') score += 2;
    if (values.weight_loss === 'yes') score += 2;
    if (values.night_sweats === 'yes') score += 1;
    
    // Testing history
    if (values.previous_test === 'no') score += 1;
    if (values.last_test_result === 'positive') score += 5;
    
    return score;
  };

  const getRiskLevel = (score) => {
    if (score >= 15) return { level: 'High', color: 'red', message: 'High risk detected. We strongly recommend immediate HIV testing and consultation.' };
    if (score >= 8) return { level: 'Moderate', color: 'yellow', message: 'Moderate risk detected. We recommend HIV testing and risk reduction counseling.' };
    if (score >= 3) return { level: 'Low-Moderate', color: 'orange', message: 'Some risk factors present. Consider HIV testing and prevention strategies.' };
    return { level: 'Low', color: 'green', message: 'Low risk detected. Continue practicing safe behaviors and consider regular testing.' };
  };

  const nextStep = (values, actions) => {
    if (step < 4) {
      setStep(step + 1);
    } else {
      // Final submission
      const score = calculateRiskScore(values);
      const risk = getRiskLevel(score);
      setAssessmentResult({ score, risk, values });
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  if (assessmentResult) {
    return (
      <>
        <RequestModal />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 py-8">
          <div className='w-full flex justify-center items-center h-auto p-8 text-white font-bold text-xl' style={{ backgroundColor: 'rgba(7,59,76)' }}>
            <div className='text-center'>
              HIV Risk Assessment Results
            </div>
          </div>
          
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Your Assessment Results</h2>
                <div className={`inline-block px-6 py-3 rounded-full text-white font-semibold text-xl bg-${assessmentResult.risk.color}-500`}>
                  {assessmentResult.risk.level} Risk
                </div>
                <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
                  {assessmentResult.risk.message}
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <h3 className="text-xl font-semibold mb-4">Recommendations</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Schedule an HIV test at one of our centers</li>
                  <li>• Speak with a healthcare professional about your results</li>
                  <li>• Learn about HIV prevention methods</li>
                  <li>• Consider regular testing based on your risk factors</li>
                </ul>
              </div>
              
              <div className="text-center">
                <button 
                  onClick={() => document.getElementById('request_modal').showModal()}
                  className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Request Free Test Kit
                </button>
                <button 
                  onClick={() => {setAssessmentResult(null); setStep(1);}}
                  className="ml-4 bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300"
                >
                  Take Assessment Again
                </button>
              </div>
            </div>
          </div>
        </div>
        </>
      );
      
  }

  return (
    <>
      <div className='w-full flex justify-center items-center h-auto p-8 text-white font-bold text-xl' style={{ backgroundColor: 'rgba(7,59,76)' }}>
        <div className='text-center'>
          The result from this assessment is not final. Visit any of our centers for an accurate test.
        </div>
      </div>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Formik 
            initialValues={{
              // Demographics
              age: '',
              gender: '',
              location: '',
              relationship_status: '',
              // Sexual Health
              sexual_partners: '',
              unprotected_sex: '',
              partner_hiv_status: '',
              sex_work: '',
              // Risk Factors
              injection_drug_use: '',
              blood_transfusion: '',
              tattoo_piercing: '',
              healthcare_exposure: '',
              // Symptoms & Testing
              recent_illness: '',
              weight_loss: '',
              night_sweats: '',
              previous_test: '',
              last_test_result: ''
            }}
            validationSchema={stepValidationSchemas[step - 1]}
            onSubmit={(values, actions) => {
              nextStep(values, actions);
            }}
          >
            {formik => (
              <Form className="bg-white rounded-lg shadow-lg overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-teal-600 to-blue-600 px-8 py-6">
                  <h2 className="text-2xl font-bold text-white text-center">HIV Risk Assessment</h2>
                  <p className="text-teal-100 text-center mt-2">Step {step} of 4</p>
                </div>

                {/* Progress Bar */}
                <div className="px-8 py-4 bg-gray-50">
                  <div className="flex justify-between items-center">
                    {[1, 2, 3, 4].map((stepNum) => (
                      <div key={stepNum} className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                          step >= stepNum ? 'bg-teal-600 text-white' : 'bg-gray-300 text-gray-600'
                        }`}>
                          {stepNum}
                        </div>
                        {stepNum < 4 && (
                          <div className={`w-16 h-1 mx-2 ${step > stepNum ? 'bg-teal-600' : 'bg-gray-300'}`} />
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-2 text-sm text-gray-600">
                    <span>Demographics</span>
                    <span>Sexual Health</span>
                    <span>Risk Factors</span>
                    <span>Symptoms</span>
                  </div>
                </div>

                {/* Form Content */}
                <div className="px-8 py-8">
                  {step === 1 && (
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-gray-800 mb-6">Basic Information</h3>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-gray-700 font-medium mb-2">Age</label>
                          <Field name="age" type="number" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                          <ErrorMessage name="age" component="div" className="text-red-600 text-sm mt-1" />
                        </div>

                        <div>
                          <label className="block text-gray-700 font-medium mb-2">Gender</label>
                          <Field as="select" name="gender" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                            <option value="">Select gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="non-binary">Non-binary</option>
                            <option value="prefer-not-to-say">Prefer not to say</option>
                          </Field>
                          <ErrorMessage name="gender" component="div" className="text-red-600 text-sm mt-1" />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-gray-700 font-medium mb-2">Location Type</label>
                          <Field as="select" name="location" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                            <option value="">Select location type</option>
                            <option value="urban">Urban</option>
                            <option value="suburban">Suburban</option>
                            <option value="rural">Rural</option>
                          </Field>
                          <ErrorMessage name="location" component="div" className="text-red-600 text-sm mt-1" />
                        </div>

                        <div>
                          <label className="block text-gray-700 font-medium mb-2">Relationship Status</label>
                          <Field as="select" name="relationship_status" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                            <option value="">Select status</option>
                            <option value="single">Single</option>
                            <option value="in-relationship">In a relationship</option>
                            <option value="married">Married</option>
                            <option value="divorced">Divorced</option>
                            <option value="widowed">Widowed</option>
                          </Field>
                          <ErrorMessage name="relationship_status" component="div" className="text-red-600 text-sm mt-1" />
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-gray-800 mb-6">Sexual Health History</h3>
                      
                      <div>
                        <label className="block text-gray-700 font-medium mb-3">How many sexual partners have you had in the last 12 months?</label>
                        <Field as="select" name="sexual_partners" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                          <option value="">Select option</option>
                          <option value="none">None</option>
                          <option value="one">One</option>
                          <option value="2-5">2-5 partners</option>
                          <option value="multiple">More than 5</option>
                        </Field>
                        <ErrorMessage name="sexual_partners" component="div" className="text-red-600 text-sm mt-1" />
                      </div>

                      <div>
                        <label className="block text-gray-700 font-medium mb-3">How often do you have unprotected sex?</label>
                        <Field as="select" name="unprotected_sex" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                          <option value="">Select option</option>
                          <option value="never">Never</option>
                          <option value="rarely">Rarely</option>
                          <option value="sometimes">Sometimes</option>
                          <option value="often">Often</option>
                          <option value="always">Always</option>
                        </Field>
                        <ErrorMessage name="unprotected_sex" component="div" className="text-red-600 text-sm mt-1" />
                      </div>

                      <div>
                        <label className="block text-gray-700 font-medium mb-3">Do you know the HIV status of your sexual partner(s)?</label>
                        <Field as="select" name="partner_hiv_status" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                          <option value="">Select option</option>
                          <option value="negative">HIV negative</option>
                          <option value="positive">HIV positive</option>
                          <option value="unknown">Unknown</option>
                          <option value="no-partners">No sexual partners</option>
                        </Field>
                        <ErrorMessage name="partner_hiv_status" component="div" className="text-red-600 text-sm mt-1" />
                      </div>

                      <div>
                        <label className="block text-gray-700 font-medium mb-3">Have you ever given or received money, drugs, or goods for sex?</label>
                        <Field as="select" name="sex_work" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                          <option value="">Select option</option>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                          <option value="prefer-not-to-say">Prefer not to say</option>
                        </Field>
                        <ErrorMessage name="sex_work" component="div" className="text-red-600 text-sm mt-1" />
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-gray-800 mb-6">Risk Factors</h3>
                      
                      <div>
                        <label className="block text-gray-700 font-medium mb-3">Have you ever injected drugs or shared needles?</label>
                        <Field as="select" name="injection_drug_use" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                          <option value="">Select option</option>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                          <option value="prefer-not-to-say">Prefer not to say</option>
                        </Field>
                        <ErrorMessage name="injection_drug_use" component="div" className="text-red-600 text-sm mt-1" />
                      </div>

                      <div>
                        <label className="block text-gray-700 font-medium mb-3">Have you received a blood transfusion in the last 5 years?</label>
                        <Field as="select" name="blood_transfusion" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                          <option value="">Select option</option>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                          <option value="unsure">Unsure</option>
                        </Field>
                        <ErrorMessage name="blood_transfusion" component="div" className="text-red-600 text-sm mt-1" />
                      </div>

                      <div>
                        <label className="block text-gray-700 font-medium mb-3">Have you had tattoos or piercings with unsterilized equipment?</label>
                        <Field as="select" name="tattoo_piercing" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                          <option value="">Select option</option>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                          <option value="unsure">Unsure</option>
                        </Field>
                        <ErrorMessage name="tattoo_piercing" component="div" className="text-red-600 text-sm mt-1" />
                      </div>

                      <div>
                        <label className="block text-gray-700 font-medium mb-3">Have you had accidental exposure to blood in healthcare settings?</label>
                        <Field as="select" name="healthcare_exposure" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                          <option value="">Select option</option>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                          <option value="unsure">Unsure</option>
                        </Field>
                        <ErrorMessage name="healthcare_exposure" component="div" className="text-red-600 text-sm mt-1" />
                      </div>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="space-y-6">
                      <h3 className="text-xl font-semibold text-gray-800 mb-6">Symptoms & Testing History</h3>
                      
                      <div>
                        <label className="block text-gray-700 font-medium mb-3">Have you had flu-like symptoms recently (fever, fatigue, swollen lymph nodes)?</label>
                        <Field as="select" name="recent_illness" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                          <option value="">Select option</option>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                          <option value="unsure">Unsure</option>
                        </Field>
                        <ErrorMessage name="recent_illness" component="div" className="text-red-600 text-sm mt-1" />
                      </div>

                      <div>
                        <label className="block text-gray-700 font-medium mb-3">Have you experienced unexplained weight loss?</label>
                        <Field as="select" name="weight_loss" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                          <option value="">Select option</option>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                          <option value="unsure">Unsure</option>
                        </Field>
                        <ErrorMessage name="weight_loss" component="div" className="text-red-600 text-sm mt-1" />
                      </div>

                      <div>
                        <label className="block text-gray-700 font-medium mb-3">Do you experience night sweats?</label>
                        <Field as="select" name="night_sweats" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                          <option value="">Select option</option>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                          <option value="sometimes">Sometimes</option>
                        </Field>
                        <ErrorMessage name="night_sweats" component="div" className="text-red-600 text-sm mt-1" />
                      </div>

                      <div>
                        <label className="block text-gray-700 font-medium mb-3">Have you ever been tested for HIV before?</label>
                        <Field as="select" name="previous_test" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                          <option value="">Select option</option>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </Field>
                        <ErrorMessage name="previous_test" component="div" className="text-red-600 text-sm mt-1" />
                      </div>

                      {formik.values.previous_test === 'yes' && (
                        <div>
                          <label className="block text-gray-700 font-medium mb-3">What was your last test result?</label>
                          <Field as="select" name="last_test_result" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                            <option value="">Select option</option>
                            <option value="negative">Negative</option>
                            <option value="positive">Positive</option>
                            <option value="inconclusive">Inconclusive</option>
                            <option value="dont-remember">Don't remember</option>
                          </Field>
                          <ErrorMessage name="last_test_result" component="div" className="text-red-600 text-sm mt-1" />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <div className="px-8 py-6 bg-gray-50 flex justify-between">
                  <button
                    type="button"
                    onClick={prevStep}
                    className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                      step === 1 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-gray-500 text-white hover:bg-gray-600'
                    }`}
                    disabled={step === 1}
                  >
                    Previous
                  </button>
                  
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    {step === 4 ? 'Get Results' : 'Next'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
}

export default Assessment
